/*
  ctx-circuit.js — product circuits (22 Oct 2026). Drives every
  [data-ctx-circuit] block rendered by snippets/ctx-circuit.liquid, on the PDP
  and on the circuits page.

  - Ticking / unticking pieces re-prices the circuit. The 5% is shown only
    while 5+ pieces are ticked, because that is the rule the discount function
    applies at checkout; fewer than 5 are added as plain singles.
  - "Add" puts one of each ticked piece in the cart with `_circuit_id` (the
    circuit metaobject GID, which the function checks against each product's
    own custom.circuit metafield) and `_circuit` (the name, for staff reading
    the order).
  - Pieces ALREADY in the cart are not added twice: their line is re-tagged
    instead, and the properties they already carry (_bundle_id, _covers, ...)
    are kept — cart/change.js replaces the whole properties object, so the
    existing ones are merged in first.
  - The display figures here are informational. Checkout is the authority.
*/
(function () {
  if (window.ctxCircuitInit) return;
  window.ctxCircuitInit = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  function money(cents) {
    if (window.Shopify && Shopify.formatMoney) {
      try { return Shopify.formatMoney(cents, window.ctxMoneyFormat || '${{amount_no_decimals}}'); } catch (e) { /* fall through */ }
    }
    var dollars = cents / 100;
    return '$' + dollars.toLocaleString('en-US', {
      minimumFractionDigits: dollars % 1 ? 2 : 0,
      maximumFractionDigits: 2
    });
  }

  function getCart() {
    return fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; });
  }

  function postJSON(url, body) {
    return fetch(root + url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) { return { ok: r.ok, data: data }; });
      });
  }

  function update(box) {
    var min = parseInt(box.getAttribute('data-min'), 10) || 5;
    var checks = box.querySelectorAll('.ctx-circuit__check');
    var count = 0;
    var total = 0;
    checks.forEach(function (c) {
      if (c.checked) { count++; total += parseInt(c.getAttribute('data-price'), 10) || 0; }
    });
    var qualifies = count >= min;
    var after = Math.round(total * 0.95);

    var labelBefore = box.querySelector('[data-label-before]');
    var before = box.querySelector('[data-total-before]');
    var afterRow = box.querySelector('[data-after-row]');
    var afterEl = box.querySelector('[data-total-after]');
    var note = box.querySelector('[data-note]');
    var add = box.querySelector('[data-add]');

    if (labelBefore) labelBefore.textContent = count + (count === 1 ? ' piece' : ' pieces') + ' at regular price';
    if (before) before.textContent = money(total);
    if (afterRow) afterRow.hidden = !qualifies;
    if (afterEl) afterEl.textContent = money(after);
    if (note) {
      note.classList.toggle('is-warn', !qualifies && count > 0);
      if (qualifies) note.textContent = 'You save ' + money(total - after) + '.';
      else if (count === 0) note.textContent = 'Tick the pieces you want.';
      else note.textContent = 'Pick at least ' + min + ' pieces for the 5% circuit price. With ' + count + ' you pay the regular price.';
    }
    if (add) {
      add.disabled = count === 0;
      if (box.classList.contains('ctx-circuit--card') || count !== checks.length) {
        add.textContent = 'Add ' + count + (count === 1 ? ' piece' : ' pieces') + ' to cart';
      }
    }
  }

  function refreshDrawer() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    /* Same refresh as ctx-pdp-bundler: the drawer was rendered with the page
       (and carries `is-empty` while the cart was empty), so swap in a fresh
       render before opening. NOT drawer.renderContents() — it throws on this
       header, which has no cart-icon-bubble section. */
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') {
      window.location.href = root + 'cart';
      return;
    }
    fetch(root + '?section_id=cart-drawer')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
          var live = document.querySelector('#CartDrawer');
          if (fresh && live) live.innerHTML = fresh.innerHTML;
        }
      })
      .catch(function () { /* the items are in the cart; only the refresh failed */ })
      .then(function () {
        drawer.classList.remove('is-empty');
        var overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
        drawer.open();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      });
  }

  function addCircuit(box, btn) {
    var circuitId = box.getAttribute('data-circuit-id');
    var circuitName = box.getAttribute('data-circuit-name');
    var err = box.querySelector('[data-error]');
    var picked = [];
    box.querySelectorAll('.ctx-circuit__check').forEach(function (c) {
      if (c.checked) picked.push(String(c.getAttribute('data-variant')));
    });
    if (!picked.length) return;

    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    if (err) { err.hidden = true; err.textContent = ''; }

    var tag = { _circuit_id: circuitId, _circuit: circuitName };
    var failed = [];

    getCart().then(function (cart) {
      var inCart = {};
      (cart.items || []).forEach(function (it) {
        var vid = String(it.variant_id);
        if (picked.indexOf(vid) !== -1 && !inCart[vid]) inCart[vid] = it;
      });
      var toAdd = picked.filter(function (vid) { return !inCart[vid]; }).map(function (vid) {
        return { id: Number(vid), quantity: 1, properties: tag };
      });
      var toTag = Object.keys(inCart).map(function (vid) { return inCart[vid]; }).filter(function (it) {
        return !it.properties || it.properties._circuit_id !== circuitId;
      });

      var adding = !toAdd.length ? Promise.resolve() : postJSON('cart/add.js', { items: toAdd }).then(function (r) {
        if (r.ok) return;
        /* One piece sold in the meantime fails the whole batch. Retry one by
           one so the rest still go in, and say which did not. */
        return toAdd.reduce(function (p, item) {
          return p.then(function () {
            return postJSON('cart/add.js', { items: [item] }).then(function (one) {
              if (!one.ok) failed.push(item.id);
            });
          });
        }, Promise.resolve());
      });

      /* Sequential, by line KEY: re-tagging changes that line's key but leaves
         every other line's key alone, so the keys read above stay valid. */
      return adding.then(function () {
        return toTag.reduce(function (p, it) {
          return p.then(function () {
            var props = {};
            var existing = it.properties || {};
            Object.keys(existing).forEach(function (k) {
              if (existing[k] !== null && existing[k] !== '') props[k] = existing[k];
            });
            props._circuit_id = circuitId;
            props._circuit = circuitName;
            return postJSON('cart/change.js', { id: it.key, quantity: it.quantity, properties: props })
              .catch(function () { /* already in the cart; tagging is best effort */ });
          });
        }, Promise.resolve());
      });
    }).then(function () {
      if (failed.length && failed.length === picked.length) throw new Error('Those pieces just sold. Refresh the page to see what is left.');
      if (failed.length && err) {
        err.textContent = failed.length + (failed.length === 1 ? ' piece' : ' pieces') + ' just sold and could not be added. The rest are in your cart.';
        err.hidden = false;
      }
      btn.textContent = 'Added ✓';
      refreshDrawer();
      setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1800);
    }).catch(function (e) {
      btn.textContent = original;
      btn.disabled = false;
      if (err) { err.textContent = (e && e.message) || 'Could not add the circuit. Please try again.'; err.hidden = false; }
    });
  }

  function init(box) {
    if (box.__ctxCircuit) return;
    box.__ctxCircuit = true;
    box.addEventListener('change', function (e) {
      if (e.target && e.target.classList.contains('ctx-circuit__check')) update(box);
    });
    var btn = box.querySelector('[data-add]');
    if (btn) btn.addEventListener('click', function () { addCircuit(box, btn); });
    update(box);
  }

  function boot() { document.querySelectorAll('[data-ctx-circuit]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
